// len mute · hat sprint — deal one word, then build a sentence from chips
// that puts it into each of its hats (noun, verb, modifier) in turn.
//
// The parser below covers exactly the grammar the chip bank can produce:
//   [subject] li [predicate] (e [object])* — with bare mi/sina dropping li,
//   extra li-predicates, pi groups, preverbs, and predicate prepositions.
// It assigns every content word a role by position, the same taxonomy the
// role roulette teaches. Position can't resolve every real ambiguity in
// toki pona (e.g. "tomo tawa mi"), so the tie-breaks here are chosen to
// match how the roulette content tags the same shapes: words trailing a
// noun head are modifiers; preverbs/prepositions only get their special
// hats inside the predicate.

import { WORDS } from "./tokiPonaData";
import { PREVERBS, PREPOSITIONS, rolesForWord } from "./tokiPonaRoles";

export const SPRINT_HATS = ["noun", "verb", "modifier"];
export const SPRINT_PARTICLES = ["li", "e", "pi"];

const isWord = (t) => Boolean(WORDS[t]);

// ---------------------------------------------------------------------------
// parsing

// Label tokens[s..e) as a trailing sequence inside a phrase whose head is
// already labeled. `preps` allows preposition detection (predicates only).
function labelTail(tokens, s, e, roles, { preps = false } = {}) {
  let sawPi = false;
  let expectNoun = false; // the word right after a preposition is its noun
  for (let k = s; k < e; k++) {
    const t = tokens[k];
    if (t === "pi") {
      let count = 0;
      for (let j = k + 1; j < e && tokens[j] !== "pi"; j++) count++;
      if (count < 2)
        return "pi needs at least two words after it — for one word, drop the pi";
      sawPi = true;
      expectNoun = false;
      continue;
    }
    if (
      preps &&
      !sawPi &&
      PREPOSITIONS.includes(t) &&
      k + 1 < e &&
      isWord(tokens[k + 1])
    ) {
      roles[k] = "preposition";
      expectNoun = true;
      continue;
    }
    roles[k] = expectNoun ? "noun" : "modifier";
    expectNoun = false;
  }
  return null;
}

// Label tokens[s..e) as a noun phrase: head noun, the rest modifiers.
function labelNounPhrase(tokens, s, e, roles) {
  if (s >= e) return "there's a gap where a word should be";
  if (!isWord(tokens[s]))
    return `"${tokens[s]}" can't start a phrase — lead with a word`;
  roles[s] = "noun";
  return labelTail(tokens, s + 1, e, roles, { preps: false });
}

// Label tokens[s..e) as a predicate phrase (the part after li / bare mi-sina).
function labelPredicate(tokens, s, e, roles) {
  if (s >= e) return "the sentence stops before saying what happens";
  if (!isWord(tokens[s]))
    return `"${tokens[s]}" can't start the predicate — lead with a word`;
  roles[s] = "verb";
  let tail = s + 1;
  const nextIsPlain = tail < e && isWord(tokens[tail]);
  if (PREVERBS.includes(tokens[s]) && nextIsPlain) {
    // mi wile moku — wile is a preverb, moku the real verb
    roles[s] = "preverb";
    roles[tail] = "verb";
    tail += 1;
  } else if (PREPOSITIONS.includes(tokens[s]) && nextIsPlain) {
    // mi tawa tomo — tawa is the predicate, tomo its noun complement
    roles[tail] = "noun";
    tail += 1;
  }
  return labelTail(tokens, tail, e, roles, { preps: true });
}

// One li-separated chunk: predicate, then any number of e-objects.
function labelPredicateChunk(tokens, s, e, roles) {
  const parts = [];
  let ps = s;
  for (let k = s; k < e; k++) {
    if (tokens[k] === "e") {
      parts.push([ps, k]);
      ps = k + 1;
    }
  }
  parts.push([ps, e]);
  const [vs, ve] = parts[0];
  if (vs >= ve) return "e needs a verb in front of it";
  const err = labelPredicate(tokens, vs, ve, roles);
  if (err) return err;
  for (let p = 1; p < parts.length; p++) {
    const [os, oe] = parts[p];
    if (os >= oe) return "e needs a word after it";
    const objErr = labelNounPhrase(tokens, os, oe, roles);
    if (objErr) return objErr;
  }
  return null;
}

// Parse a chip sentence. Returns { ok: false, error } for ungrammatical
// input, or { ok: true, roles: [{ tok, role }] } where particles get
// role null.
export function analyzeSentence(tokens) {
  if (!tokens || tokens.length === 0)
    return { ok: false, error: "tap some words to build a sentence first" };
  for (const t of tokens) {
    if (!isWord(t) && !SPRINT_PARTICLES.includes(t))
      return { ok: false, error: `"${t}" isn't on the shelf for this game` };
  }

  const roles = new Array(tokens.length).fill(null);
  const liAt = tokens.indexOf("li");
  let predStart;

  if (liAt === -1) {
    if (tokens[0] !== "mi" && tokens[0] !== "sina")
      return {
        ok: false,
        error:
          "a sentence needs li between the subject and the rest — only bare mi / sina skip it",
      };
    roles[0] = "noun";
    predStart = 1;
  } else {
    if (liAt === 0)
      return { ok: false, error: "li needs a subject in front of it" };
    const subject = tokens.slice(0, liAt);
    if (subject.length === 1 && (subject[0] === "mi" || subject[0] === "sina"))
      return {
        ok: false,
        error: `with ${subject[0]} alone, drop the li — "${subject[0]} moku", not "${subject[0]} li moku"`,
      };
    if (subject.includes("e"))
      return { ok: false, error: "e belongs after the verb, not in the subject" };
    const err = labelNounPhrase(tokens, 0, liAt, roles);
    if (err) return { ok: false, error: err };
    predStart = liAt + 1;
  }

  if (predStart >= tokens.length)
    return {
      ok: false,
      error: "the sentence stops before saying what the subject does or is",
    };

  // remaining li tokens split off extra predicates: ona li moku li lape
  const chunks = [];
  let cs = predStart;
  for (let k = predStart; k < tokens.length; k++) {
    if (tokens[k] === "li") {
      chunks.push([cs, k]);
      cs = k + 1;
    }
  }
  chunks.push([cs, tokens.length]);
  for (const [s, e] of chunks) {
    if (s >= e) return { ok: false, error: "li needs something after it" };
    const err = labelPredicateChunk(tokens, s, e, roles);
    if (err) return { ok: false, error: err };
  }

  return {
    ok: true,
    roles: tokens.map((tok, i) => ({ tok, role: roles[i] })),
  };
}

// Where did the target word land? { found: n, role } — role is the hat the
// first occurrence wears.
export function targetRole(analysis, word) {
  const hits = analysis.roles.filter((r) => r.tok === word);
  return { found: hits.length, role: hits.length ? hits[0].role : null };
}

// ---------------------------------------------------------------------------
// dealing sprints

// Hats a word can honestly wear in this mode (backed by roulette content).
export function hatsFor(word) {
  const roles = rolesForWord(word);
  return SPRINT_HATS.filter((h) => roles.includes(h));
}

// Words worth sprinting: at least two of the three basic hats.
export function sprintWords(available) {
  return available.filter((w) => isWord(w) && hatsFor(w).length >= 2);
}

// Versatile little words that make almost any sentence buildable.
const HELPER_FAVORITES = [
  "mi",
  "sina",
  "ona",
  "jan",
  "ni",
  "pona",
  "suli",
  "mute",
  "lukin",
  "tomo",
  "moku",
  "tenpo",
];

function shuffle(list, rng) {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Deal a chip bank for one target word: a handful of dependable helpers plus
// some variety from the learner's own vocabulary.
export function dealSprint(word, available, rng = Math.random) {
  const favorites = HELPER_FAVORITES.filter(
    (w) => w !== word && available.includes(w)
  );
  const rest = available.filter((w) => w !== word && !favorites.includes(w));
  const helpers = [...favorites.slice(0, 6), ...shuffle(rest, rng)]
    .slice(0, 9)
    .sort();
  return { word, hats: hatsFor(word), particles: SPRINT_PARTICLES, helpers };
}

// Pick a word from the unlocked pool and deal it. Null when the pool is
// too thin (the home screen gates the mode, so normally it never is).
export function buildSprint(available, rng = Math.random) {
  const pool = sprintWords(available);
  if (!pool.length) return null;
  const word = pool[Math.floor(rng() * pool.length)];
  return dealSprint(word, available, rng);
}
